import React from "react";
import LegalShell from "@/components/LegalShell";

export default function Terms() {
  return (
    <LegalShell title="Terms of Service" subtitle="Effective February 28, 2026 · Version 1.0">
      <Section title="1. What WarPaint DFS is">
        <p>
          WarPaint DFS (the "Service") is a subscription-based analytics platform that generates
          DraftKings-compatible Daily Fantasy Sports NFL lineups using a proprietary rule-based
          Integer Linear Programming (ILP) optimizer. We are an <strong className="text-white">independent
          software product</strong>. WarPaint DFS is <strong className="text-white">not affiliated
          with, endorsed by, sponsored by, or connected to DraftKings Inc., the National Football
          League, or any team, coach, or player.</strong>
        </p>
      </Section>

      <Section title="2. Who can use the Service">
        You must be at least eighteen (18) years old — or twenty-one (21) if you reside in
        Massachusetts, Iowa, or Louisiana — to register an account or purchase a subscription. You
        may not access WarPaint DFS from any U.S. state where paid-entry daily fantasy sports is
        prohibited (currently: Arizona, Hawaii, Idaho, Louisiana, Montana, Nevada, and Washington
        — subject to change). You certify all of the above by clicking "I agree" during signup.
      </Section>

      <Section title="3. Subscriptions & billing">
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Subscriptions are billed in advance through Stripe. You authorize Stripe to charge
              the payment method on file for the subscription plan you select.</li>
          <li><strong className="text-white">Weekly plan ($99 / week)</strong> renews automatically every 7 days
              until cancelled from your account page or by emailing{" "}
              <a href="mailto:support@warpaintdfs.com" className="text-[#39FF14] underline">support@warpaintdfs.com</a>.
              Cancelling stops future charges; access continues through the end of the week already paid for.</li>
          <li>Legacy monthly and season subscriptions purchased before the weekly plan keep their original
              terms until they expire and are not renewed.</li>
          <li>All prices are in United States Dollars (USD). Applicable taxes are calculated and
              collected by Stripe based on your billing address.</li>
        </ul>
      </Section>

      <Section title="4. Refunds">
        <p>
          See our full <a href="/refunds" className="text-[#39FF14] underline">Refund Policy</a>.
          Summary: 7-day money-back guarantee on your first charge; a full-week refund
          for platform outages exceeding 4 hours during a Sunday main slate; all other refund
          requests are handled at our sole discretion.
        </p>
      </Section>

      <Section title="5. No gambling advice · no guarantee of winnings">
        <p>
          WarPaint DFS produces mathematical lineup constructions based on publicly available
          projections, ownership, and Vegas market data. <strong className="text-white">We do not
          predict outcomes. We do not guarantee any user will win any contest.</strong> Past
          performance of the optimizer, backtests, or any user's lineups is not a guarantee of
          future results. Daily fantasy sports contests carry a substantial risk of financial loss.
          Never wager more than you can afford to lose. If gambling is causing you or someone you
          know harm, call the National Council on Problem Gambling helpline at{" "}
          <strong className="text-white">1-800-GAMBLER</strong> (available 24/7). See our{" "}
          <a href="/responsible-gambling" className="text-[#39FF14] underline">Responsible Gambling</a> page.
        </p>
      </Section>

      <Section title="6. Acceptable use">
        <p>You agree NOT to:</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Scrape, resell, sublicense, or redistribute WarPaint DFS projections, ownership
              estimates, generated lineups, or any other output of the Service.</li>
          <li>Share your login credentials with any other person or automated system.</li>
          <li>Use the Service to violate DraftKings' own Terms of Service (including but not
              limited to entering more lineups than DK permits per contest).</li>
          <li>Attempt to reverse-engineer, decompile, or otherwise derive the source code or
              algorithm from the compiled or hosted Service.</li>
          <li>Use the Service in any way that would defraud, harass, or defame any other person.</li>
        </ul>
        <p className="mt-3">Violations may result in immediate suspension without refund.</p>
      </Section>

      <Section title="7. Intellectual property">
        All software, algorithms, UI, copy, branding, and documentation are the exclusive property
        of WarPaint DFS. You retain ownership of any lineups you generate but grant WarPaint DFS
        a perpetual, royalty-free license to use anonymized, aggregated versions of your generation
        activity to improve the Service.
      </Section>

      <Section title="8. Third-party services">
        <p>The Service integrates with:</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Stripe (payment processing)</li>
          <li>DraftKings public draftgroups API (player pools, salaries)</li>
          <li>SportsGameOdds (Vegas market data)</li>
          <li>Sleeper (injury feeds, depth charts) · Open-Meteo (weather) · nflverse (historical stats)</li>
          <li>Anthropic Claude (AI attack plan generation)</li>
          <li>Resend (transactional emails)</li>
          <li>Sentry (error monitoring)</li>
        </ul>
        <p className="mt-3">Your use of the Service constitutes acceptance of the applicable
          Terms and Privacy policies of each of these providers.</p>
      </Section>

      <Section title="8a. User-provided data (Field Model uploads)">
        <p>The Field Model feature lets you upload projection and ownership files to your own account.
          WarPaint DFS does not source, license, sell, pool or redistribute third-party projections.</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>You represent that you hold the rights to any file you upload and that your use of it
              within the Service for your own play complies with the terms of the service it came from.
              You are solely responsible for that compliance.</li>
          <li>Uploaded files are processed for your account only. They are never shared with, or used to
              generate output for, any other user. Only derived aggregates (consensus median, maximum
              ownership, coverage percentage) are displayed back to you.</li>
          <li>WarPaint DFS is not affiliated with, endorsed by, or connected to any third-party projection
              or DFS analytics provider. Any labels you assign to uploads are yours.</li>
          <li>We may remove uploaded data or suspend the feature if we receive a credible notice that
              its use violates a third party's rights.</li>
        </ul>
      </Section>

      <Section title="9. Limitation of liability">
        To the maximum extent permitted by law, WarPaint DFS, its founders, and its employees are
        not liable for any indirect, incidental, special, consequential, or punitive damages —
        including but not limited to lost profits, lost DFS contest entries, missed lineup locks,
        or any other DFS-related financial losses — arising out of or in connection with your use
        of the Service. Our total liability for any claim shall not exceed the amount you paid
        WarPaint DFS in the six (6) months preceding the event giving rise to the claim.
      </Section>

      <Section title="10. Termination">
        You may cancel your subscription at any time from your billing page. We may suspend or
        terminate your account at any time for violation of these Terms. Upon termination, your
        subscription entitlement ends and no partial refund is owed except as provided in the
        Refund Policy.
      </Section>

      <Section title="11. Changes to these Terms">
        We may update these Terms from time to time. Material changes will be emailed to the
        address on file at least 14 days before taking effect. Continued use of the Service after
        the effective date constitutes acceptance.
      </Section>

      <Section title="12. Governing law">
        These Terms are governed by the laws of the State of Delaware, USA, without regard to its
        conflict-of-laws provisions. Any dispute will be resolved by binding arbitration in
        Wilmington, Delaware under the rules of the American Arbitration Association.
      </Section>

      <Section title="13. Contact">
        Questions about these Terms? Email{" "}
        <a href="mailto:support@warpaintdfs.com" className="text-[#39FF14] underline">support@warpaintdfs.com</a>.
      </Section>
    </LegalShell>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-white mb-2">{title}</h2>
      <div className="text-sm text-zinc-300 leading-relaxed">{children}</div>
    </div>
  );
}
